import { modernisierungsumlage as modUmlageConfig, mietpreisbremse as mpbConfig } from './mietrechtConfig';

// Calculate annual rental income for a single year
export function calculateAnnualRentalIncome(params) {
  const { rentalType = 'longterm' } = params;

  if (rentalType === 'shortterm') {
    return calculateShortTermIncome(params);
  }
  return calculateLongTermIncome(params);
}

function calculateLongTermIncome({ sqm = 75, units = 1, kaltmietePerSqm = 13, vacancyRate = 0.03 }) {
  const grossRent = sqm * kaltmietePerSqm * 12 * units;
  const vacancyLoss = grossRent * vacancyRate;
  const netRent = grossRent - vacancyLoss;
  return { grossRent, vacancyLoss, netRent };
}

function calculateShortTermIncome({ nightlyRate = 95, availableNights = 250, occupancyRate = 0.72, platformFeePercent = 15 }) {
  const grossBookings = nightlyRate * availableNights * occupancyRate;
  const platformFees = grossBookings * (platformFeePercent / 100);
  const netRent = grossBookings - platformFees;
  return { grossBookings, platformFees, netRent };
}

// Calculate multi-year revenue projection with rent increases
export function calculateMultiYearRevenue(params, years, inflationRate = 2) {
  const {
    rentalType = 'longterm',
    rentIncreaseType = 'regular',
    kaltmietePerSqm = 13,
    mietspiegelRent = null,
    kappungsgrenzeRate = 0.15,
    cpiGrowthRate = 2,
    staffelmieteAmount = 0,
    sqm = 75,
    units = 1,
    vacancyRate = 0.03,
    nightlyRate = 95,
    availableNights = 250,
    occupancyRate = 0.72,
    platformFeePercent = 15,
  } = params;

  const result = {};
  let currentRentPerSqm = kaltmietePerSqm;
  let currentMietspiegel = mietspiegelRent || kaltmietePerSqm;

  for (let year = 1; year <= years; year++) {
    if (rentalType === 'shortterm') {
      const adjustedNightlyRate = nightlyRate * Math.pow(1 + inflationRate / 100, year - 1);
      const income = calculateShortTermIncome({
        nightlyRate: adjustedNightlyRate,
        availableNights,
        occupancyRate,
        platformFeePercent,
      });
      result[year] = { ...income, nightlyRate: adjustedNightlyRate };
      continue;
    }

    // Long-term: apply rent increase logic
    if (year > 1) {
      currentMietspiegel *= (1 + inflationRate / 100);

      switch (rentIncreaseType) {
        case 'indexmiete': {
          currentRentPerSqm *= (1 + cpiGrowthRate / 100);
          break;
        }
        case 'staffelmiete': {
          const monthlyIncrease = staffelmieteAmount / (sqm * units);
          currentRentPerSqm += monthlyIncrease;
          break;
        }
        case 'regular':
        default: {
          // Apply Kappungsgrenze: max increase every 3 years
          if ((year - 1) % 3 === 0) {
            const maxRent = currentRentPerSqm * (1 + kappungsgrenzeRate);
            const targetRent = Math.min(maxRent, currentMietspiegel);
            currentRentPerSqm = Math.max(currentRentPerSqm, targetRent);
          }
          break;
        }
      }
    }

    const income = calculateLongTermIncome({
      sqm,
      units,
      kaltmietePerSqm: currentRentPerSqm,
      vacancyRate,
    });
    result[year] = { ...income, rentPerSqm: currentRentPerSqm };
  }

  return result;
}

// Modernisierungsumlage: calculate rent increase after renovation
export function calculateModernisierungsumlage(totalCost, maintenanceSharePercent, sqm, currentRentPerSqm) {
  const maintenanceShare = maintenanceSharePercent / 100;
  const eligibleCost = totalCost * (1 - maintenanceShare);
  const annualSurcharge = eligibleCost * modUmlageConfig.rate;
  const rawMonthlyPerSqm = annualSurcharge / 12 / sqm;

  // Cap based on current rent level
  const cap = currentRentPerSqm < modUmlageConfig.caps.lowRent.threshold
    ? modUmlageConfig.caps.lowRent.maxPerSqmMonth
    : modUmlageConfig.caps.standard.maxPerSqmMonth;

  const cappedMonthlyPerSqm = Math.min(rawMonthlyPerSqm, cap);
  const monthlyIncrease = Math.round(cappedMonthlyPerSqm * sqm * 100) / 100;

  return {
    eligibleCost,
    rawMonthlyPerSqm: Math.round(rawMonthlyPerSqm * 100) / 100,
    cappedMonthlyPerSqm,
    monthlyIncrease,
  };
}

// Mietpreisbremse: cap rent on re-letting
export function applyMietpreisbremse(requestedRentPerSqm, mietspiegelRent, mietpreisbremseActive) {
  if (!mietpreisbremseActive) return requestedRentPerSqm;
  const maxRent = mietspiegelRent * (1 + mpbConfig.surchargePercent);
  return Math.min(requestedRentPerSqm, maxRent);
}
